import { useMemo } from 'react';

import { FormProvider } from 'core/states';
import { PricingProfileRuleType } from 'pricing/enums';
import { useRuleActions } from 'pricing/hooks';
import { IPricingProfile, IPricingProfileRule } from 'pricing/types';

import { upsertPricingProfileRuleSchema } from './schema';
import { IPricingProfileRuleForm } from './types';
import { UpsertPricingProfileRuleFormFields } from './UpsertPricingProfileRuleFormFields';
import { getDefaultValues, mapUpsertPricingProfileRule } from './utils';

interface IProps {
  pricingProfile: IPricingProfile;
  pricingProfileRule?: IPricingProfileRule;
  type: PricingProfileRuleType;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileRule = ({
  pricingProfile,
  pricingProfileRule,
  type,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const { upsertRule } = useRuleActions();

  const structureId =
    type === PricingProfileRuleType.MARGIN
      ? pricingProfile.marginStructureId
      : pricingProfile.discountStructureId;

  const defaultValues = useMemo(() => getDefaultValues(pricingProfileRule), [pricingProfileRule]);

  const onSubmit = () => async (data: IPricingProfileRuleForm) => {
    const { success } = await upsertRule(
      mapUpsertPricingProfileRule(structureId, data, type, pricingProfileRule?.id)
    );

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess();
      onClose();
    }, 1000);
  };

  const handleOnRemoveSuccess = () => {
    setTimeout(() => {
      onRemoveSuccess?.();
      onClose();
    }, 1000);
  };

  return (
    <FormProvider
      schema={upsertPricingProfileRuleSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPricingProfileRuleFormFields
        pricingProfileRule={pricingProfileRule}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        type={type}
        onRemoveSuccess={handleOnRemoveSuccess}
      />
    </FormProvider>
  );
};
